import { useRef } from "react";
import { OUTCOME_LABELS } from "@components/scheduling/campaignLabels";
import Button from "@components/ui/Button";

export const REASON_LABELS = {
  cancelled: "Cancelled – no rebook",
  no_show: "No-show – no rebook",
  recently_seen: "Seen 1–4 months ago",
  inactive: "Dormant 5–18 months",
  long_dormant: "Not seen in 18+ months",
  inactive_pms: "Inactive in PMS",
};

/** Rows per page in a view-only list; the callable lists are never paged. */
export const PAGE_SIZE = 50;

/** "21 months ago", "Under a month ago", "None on record", or an em dash when the
 * backend predates the field. */
export function lastVisitText(row) {
  const months = row.months_since_last_visit;
  if (months === undefined) return "—";
  if (months === null) return "None on record";
  if (months === 0) return "Under a month ago";
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

/** The backend's own floor for enrolling a row: a phone, not in a campaign. */
const hasPhoneAndFree = (p) => Boolean(p.phone) && !p.in_campaign;

// One date format for every column: "Jan 1, 1980".
const DATE_FORMAT = { year: "numeric", month: "short", day: "numeric" };
const formatDate = (value) => new Date(value).toLocaleDateString("en-US", DATE_FORMAT);

const CELL = "px-3 py-2 text-sm whitespace-nowrap";
const HEAD =
  "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap";
const CHECKBOX = "w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500";

const BADGE_TONES = {
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  red: "bg-red-50 text-red-700 ring-red-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  gray: "bg-gray-100 text-gray-600 ring-gray-200",
};

function Badge({ tone, children }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${BADGE_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

/** Why a callable row cannot be ticked, in the words its badge shows. */
function ineligibleReason(patient, isEligible) {
  if (!patient.phone) return "No phone number";
  if (patient.in_campaign) return "In a campaign";
  if (patient.do_not_call) return "Do not call";
  if (!isEligible(patient)) return "Contacted recently";
  return null;
}

function Pager({ page, pageCount, total, onPageChange, className = "" }) {
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className="text-sm text-gray-600 tabular-nums">
        {`${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`}
      </span>
      <Button
        color="light"
        size="xs"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Prev
      </Button>
      <Button
        color="light"
        size="xs"
        aria-label="Next page"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </Button>
    </div>
  );
}

/**
 * The active reactivation list. A selectable list (cancellations, dormant)
 * shows every row with a checkbox, a "Select all" for the rows it shows and
 * can enroll, and the reason a row cannot be ticked as a badge by the name. A
 * view-only list (18+ months, inactive in the PMS) has no checkbox column at
 * all, says so once, and pages through PAGE_SIZE rows at a time.
 */
export default function ReactivationListSection({
  title,
  rows,
  selectable,
  showType = false,
  isEligible = hasPhoneAndFree,
  selectedIds,
  onToggleOne,
  onToggleRows,
  onMarkDoNotCall,
  busyPatientId,
  emptyText,
  page = 1,
  onPageChange,
}) {
  const eligibleRows = selectable ? rows.filter(isEligible) : [];
  const selectedCount = eligibleRows.filter((p) =>
    selectedIds.has(p.external_patient_id)
  ).length;
  const allSelected =
    eligibleRows.length > 0 && selectedCount === eligibleRows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const paged = !selectable && rows.length > PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), pageCount);
  const shown = paged
    ? rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    : rows;
  // The bottom pager is clicked at the end of the table; the next page starts
  // at the top of the list, under the pinned pill row.
  const sectionRef = useRef(null);
  const pager = (placement) =>
    paged && (
      <Pager
        page={currentPage}
        pageCount={pageCount}
        total={rows.length}
        onPageChange={(next) => {
          onPageChange(next);
          if (placement === "bottom") sectionRef.current?.scrollIntoView?.({ block: "start" });
        }}
        className={placement === "top" ? "ml-auto" : "self-start"}
      />
    );

  return (
    <section ref={sectionRef} className="flex flex-col gap-2 scroll-mt-28">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-800">
            {title} ({rows.length.toLocaleString()})
          </h2>
          {!selectable && (
            <p className="text-xs text-gray-500">
              View only. These patients can't be added to a campaign.
            </p>
          )}
        </div>
        {pager("top")}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
          {emptyText}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {selectable && (
                  <th scope="col" className={`${HEAD} w-10`}>
                    <input
                      type="checkbox"
                      aria-label={`Select all ${title.toLowerCase()}`}
                      className={CHECKBOX}
                      checked={allSelected}
                      // `indeterminate` is a DOM property with no attribute, so
                      // a partial selection reads as plain unchecked without it.
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      disabled={eligibleRows.length === 0}
                      onChange={() => onToggleRows(eligibleRows, !allSelected)}
                    />
                  </th>
                )}
                <th scope="col" className={HEAD}>Name</th>
                <th scope="col" className={HEAD}>Phone</th>
                <th scope="col" className={HEAD}>Last visit</th>
                {showType && <th scope="col" className={HEAD}>Type</th>}
                <th scope="col" className={`${HEAD} hidden md:table-cell`}>Last contact</th>
                <th scope="col" className={`${HEAD} hidden lg:table-cell`}>Date of birth</th>
                <th scope="col" className={HEAD}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shown.map((patient, idx) => {
                // The PMS does not always give us a name; the row shows an em
                // dash for it, so the controls must not read "Select null".
                const who = patient.name || "unnamed patient";
                const reason = selectable ? ineligibleReason(patient, isEligible) : null;
                return (
                  <tr key={patient.external_patient_id || idx} className="hover:bg-gray-50">
                    {selectable && (
                      <td className={`${CELL} w-10`}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${who}`}
                          title={reason || undefined}
                          className={CHECKBOX}
                          checked={selectedIds.has(patient.external_patient_id)}
                          disabled={Boolean(reason)}
                          onChange={() => onToggleOne(patient)}
                        />
                      </td>
                    )}
                    <td className={CELL}>
                      <span className="inline-flex items-center gap-2">
                        <span className="font-medium text-gray-900">{patient.name || "—"}</span>
                        {patient.in_campaign && <Badge tone="blue">In a campaign</Badge>}
                        {patient.do_not_call && <Badge tone="red">Do not call</Badge>}
                        {reason === "No phone number" && <Badge tone="gray">No phone number</Badge>}
                        {reason === "Contacted recently" && (
                          <Badge tone="amber">Contacted recently</Badge>
                        )}
                      </span>
                    </td>
                    <td className={`${CELL} text-gray-700`}>
                      {patient.phone ? (
                        <a href={`tel:${patient.phone}`} className="text-blue-600 hover:underline">
                          {patient.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className={`${CELL} text-gray-700`}>{lastVisitText(patient)}</td>
                    {showType && (
                      <td className={`${CELL} text-gray-700`}>
                        {REASON_LABELS[patient.reason] || patient.reason}
                      </td>
                    )}
                    <td className={`${CELL} hidden md:table-cell text-gray-600`}>
                      {patient.last_contacted_at ? (
                        <>
                          <span>{formatDate(patient.last_contacted_at)}</span>
                          {patient.last_outcome && (
                            <>
                              <span aria-hidden="true" className="mx-1 text-gray-400">·</span>
                              <span className="text-gray-500">
                                {OUTCOME_LABELS[patient.last_outcome] || patient.last_outcome}
                              </span>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400">Not contacted</span>
                      )}
                    </td>
                    <td className={`${CELL} hidden lg:table-cell text-gray-700`}>
                      {patient.date_of_birth ? formatDate(`${patient.date_of_birth}T00:00:00`) : "—"}
                    </td>
                    {/* py-1: the button carries its own padding, so the row
                        stays as tall as its text cells. */}
                    <td className="px-3 py-1 text-sm whitespace-nowrap">
                      <Button
                        color="light"
                        size="xs"
                        aria-label={
                          patient.do_not_call ? `Allow calls for ${who}` : `Do not call ${who}`
                        }
                        disabled={busyPatientId === patient.external_patient_id}
                        onClick={() => onMarkDoNotCall(patient)}
                      >
                        {patient.do_not_call ? "Allow calls" : "Do not call"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Left-aligned, so the page's chat launcher in the bottom-right
          corner never sits on it. */}
      {pager("bottom")}
    </section>
  );
}
